import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GoabBadge,
  GoabBlock,
  GoabContainer,
  GoabSpacer,
  GoabModal,
  GoabButton,
  GoabButtonGroup,
  GoabCallout,
} from "@abgov/react-components";
import DisplayContent from "../../MPP-Common-SPA/components/DynamicForm/Forms/DisplayContent";
import { selectCurrentUser } from "../../Store/api/auth/authSelectors";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import {
  InternalApplicationStatuses,
  VerificationStatuses,
  StatusTransitionEventEnum,
  VerificationStatusChangeEvent,
} from "../../MPP-Common-SPA/common/constants/statuses";
import {
  domainKey,
  refCategories,
  getBadgeType,
  formatDate,
  getFieldMappingValue,
  EventsEnabled,
  isPortalActive,
  isEventTypeActive,
} from "../../MPP-Common-SPA/utils/globalUtility";
import {
  useGetFieldMappingQuery,
  useGetProgramFeatureMappingByNameQuery,
} from "../../Store/api/apiSlice";

import "./applicationComponent.css";
import { PAYMENT_VALUES } from "../../MPP-Common-SPA/common/constants/values";
import { FeatureNames } from "../../MPP-Common-SPA/featureFlags/features";

const ApplicationComponent = ({
  program,
  application,
  programEvents,
  availableEvents,
  handleWithdrawApplication,
}) => {
  const {
    id: applicationId,
    internalStatus,
    applicationType,
    uniqueApplicationCode,
    updatedTime,
    applicationDataJson,
    followUpInstruction,
    verificationStatusChangeEvent,
    userDataChanged,
    statusTransitionEvent,
  } = application ?? {};

  const navigate = useNavigate();
  const { getResourceValue, getRefData, clickHandlers } =
    useSessionManagement();
  let user = useSelector(selectCurrentUser);
  const applicationData = JSON.parse(applicationDataJson ?? "{}");
  const { data: programEventFeatureMapping } =
    useGetProgramFeatureMappingByNameQuery(
      { featureName: FeatureNames.Events, programId: program?.id },
      { skip: !program?.id },
    );

  const { data: fieldMapping } = useGetFieldMappingQuery();

  const paymentPath = JSON.parse(fieldMapping ?? "{}")?.receive_payment;
  const receivePayment = getFieldMappingValue(
    applicationData?.applicationData?.stepData,
    paymentPath,
  );

  const [
    showWithdrawApplicationConfirmation,
    setShowWithdrawApplicationConfirmation,
  ] = useState(false);

  const handleApplicationClick = (applicationCode) => {
    navigate(`/application?code=${applicationCode}`);
  };

  const selectedEvent = programEvents?.find(
    (evt) => evt.code === applicationType,
  );
  const isActivePortal = isPortalActive(program, user);
  const isActiveEventType = isEventTypeActive(
    programEventFeatureMapping,
    selectedEvent,
    user,
  );

  const isEventsEnabled = EventsEnabled(programEventFeatureMapping);

  const canEdit =
    (isActivePortal && !isEventsEnabled) ||
    (isActiveEventType &&
      (internalStatus === InternalApplicationStatuses.FollowUp ||
        internalStatus === InternalApplicationStatuses.Submitted));

  const appStatusesRefData = getRefData({
    category: refCategories.APP_STATUS,
    domainKey,
  });
  const applicationStatus = appStatusesRefData?.find(
    (e) => e.code === "" + internalStatus,
  )?.description;

  const closeWithdrawApplicationConfirmation = () => {
    setShowWithdrawApplicationConfirmation(false);
    handleWithdrawApplication(applicationId);
  };

  const convertUTCToMountainTime = (utcDateStr) => {
    const date = new Date(utcDateStr + "Z");

    return formatDate(date);
  };

  const isVerifiedStatusChange =
    verificationStatusChangeEvent ===
      VerificationStatusChangeEvent.BasicToPending ||
    verificationStatusChangeEvent ===
      VerificationStatusChangeEvent.BasicToVerified;

  const isEditableStatus =
    internalStatus === InternalApplicationStatuses.Draft ||
    internalStatus === InternalApplicationStatuses.Submitted ||
    internalStatus === InternalApplicationStatuses.FollowUp;

  const showUserDataChangedCallouts = userDataChanged && isEditableStatus;

  return (
    <>
      <GoabContainer mt="xl" mb="none">
        {showUserDataChangedCallouts &&
          internalStatus === InternalApplicationStatuses.Draft &&
          statusTransitionEvent !==
            StatusTransitionEventEnum.SubmittedToDraft && (
            <GoabCallout
              type="information"
              heading="Your Alberta.ca Account information has changed"
            >
              Please review your application details to make sure the
              information is accurate.
            </GoabCallout>
          )}

        {showUserDataChangedCallouts &&
          (statusTransitionEvent ===
            StatusTransitionEventEnum.SubmittedToDraft ||
            internalStatus === InternalApplicationStatuses.FollowUp) && (
            <GoabCallout type="important" heading="Resubmit your application">
              Your application has been updated with changes from your
              Alberta.ca Account. Please review the application details and
              resubmit your application.
            </GoabCallout>
          )}
        {isEventsEnabled && !isActiveEventType && (
          <GoabCallout
            type="information"
            heading={getResourceValue({
              key: "RESOURCES@callout.inactiveEvent.header",
              params: { eventName: selectedEvent.name ?? applicationType },
            })}
            size="large"
          >
            <DisplayContent
              content={getResourceValue({
                key: "RESOURCES@callout.inactiveEvent.content",
              })}
            />
          </GoabCallout>
        )}
        {/* displays the account verification callout */}
        {(isActiveEventType || !isEventsEnabled) &&
          user.verificationStatus === VerificationStatuses.Basic &&
          internalStatus === InternalApplicationStatuses.FollowUp &&
          !followUpInstruction && (
            <GoabCallout
              type="important"
              heading={getResourceValue({
                key: "RESOURCES@callout.basicAccountFollowUp.header",
              })}
              size="large"
            >
              <DisplayContent
                content={getResourceValue({
                  key: "RESOURCES@callout.basicAccountFollowUp.content",
                  params: {
                    url: getResourceValue({
                      key: "RESOURCES@clickhandler.verify_account",
                    }),
                    url2: getResourceValue({
                      key: "RESOURCES@url.alberta_supports",
                    }),
                  },
                })}
                clickHandlers={{
                  verifyAccount: () => clickHandlers.verifyAccount(),
                }}
              />
            </GoabCallout>
          )}
        {/* account status changed - update payment details */}
        {(isActiveEventType || !isEventsEnabled) &&
          isVerifiedStatusChange &&
          (internalStatus === InternalApplicationStatuses.Draft ||
            internalStatus === InternalApplicationStatuses.FollowUp) &&
          !!applicationData?.applicationData?.inReviewMode && (
            <GoabCallout
              type="important"
              heading={getResourceValue({
                key:
                  internalStatus === InternalApplicationStatuses.Draft
                    ? "RESOURCES@callout.verificationStatusDraftUpdated.header"
                    : "RESOURCES@callout.verificationStatusUpdated.header",
              })}
              size="large"
            >
              <DisplayContent
                content={getResourceValue({
                  key:
                    internalStatus === InternalApplicationStatuses.Draft
                      ? "RESOURCES@callout.verificationStatusDraftUpdated.content"
                      : "RESOURCES@callout.verificationStatusUpdated.content",
                })}
              />
            </GoabCallout>
          )}
        {/* opted to visit support center */}
        {(isActiveEventType || !isEventsEnabled) &&
          !isVerifiedStatusChange &&
          user.verificationStatus !== VerificationStatuses.Basic &&
          internalStatus === InternalApplicationStatuses.FollowUp &&
          receivePayment !== PAYMENT_VALUES.ETransfer &&
          !followUpInstruction && (
            <GoabCallout
              type="important"
              heading={getResourceValue({
                key: "RESOURCES@callout.verifiedAccountFollowUp.header",
              })}
              size="large"
            >
              <DisplayContent
                content={getResourceValue({
                  key: "RESOURCES@callout.verifiedAccountFollowUp.content",
                  params: {
                    url: getResourceValue({
                      key: "RESOURCES@url.alberta_supports",
                    }),
                  },
                })}
              />
            </GoabCallout>
          )}

        {/* followUpInstruction */}
        {(isActiveEventType || !isEventsEnabled) &&
          internalStatus === InternalApplicationStatuses.FollowUp &&
          !!followUpInstruction && (
            <GoabCallout
              type="important"
              heading={getResourceValue({
                key: "RESOURCES@callout.verifiedAccountFollowUpInstruction.header",
              })}
              size="large"
            >
              <DisplayContent
                content={getResourceValue({
                  key: "RESOURCES@callout.verifiedAccountFollowUpInstruction.content",
                  params: { followUpInstruction },
                })}
              />
            </GoabCallout>
          )}
        {/* application status in the tile   */}
        {internalStatus && (
          <div className="app-status">
            <GoabBadge
              type={getBadgeType(internalStatus)}
              content={getResourceValue({
                key: "RESOURCES@global.applicationStatus",
                params: { applicationStatus: applicationStatus },
              })}
              mt="2xs"
            ></GoabBadge>
            <div>{convertUTCToMountainTime(updatedTime)}</div>
          </div>
        )}
        {/* application tile on landing page */}
        <div className="container-example-2--container">
          <GoabBlock direction="column" mt="l" gap="2xs" alignment="start">
            <h3>{selectedEvent?.name ?? applicationType}</h3>
            <GoabSpacer vSpacing="m" />
            <DisplayContent
              content={getResourceValue({
                key: "RESOURCES@global.applicationCode",
                params: { uniqueApplicationCode },
              })}
            />
          </GoabBlock>
          <GoabBlock direction="column" mt="l" gap="2xs" alignment="end">
            <GoabButton
              type={
                canEdit ||
                ((isActiveEventType || !isEventsEnabled) &&
                  internalStatus === InternalApplicationStatuses.Draft)
                  ? "primary"
                  : "secondary"
              }
              onClick={() => handleApplicationClick(uniqueApplicationCode)}
            >
              {(isActiveEventType || !isEventsEnabled) &&
              internalStatus === InternalApplicationStatuses.Draft
                ? getResourceValue({
                    key: "RESOURCES@button.continueApplication",
                  })
                : canEdit
                  ? getResourceValue({
                      key: "RESOURCES@button.manageApplication",
                    })
                  : getResourceValue({
                      key: "RESOURCES@button.viewApplication",
                    })}
            </GoabButton>
            {(isActiveEventType || !isEventsEnabled) &&
              internalStatus === InternalApplicationStatuses.Draft && (
                <GoabButton
                  type="tertiary"
                  mt="3xs"
                  onClick={() => setShowWithdrawApplicationConfirmation(true)}
                >
                  {getResourceValue({
                    key: "RESOURCES@button.withdrawApplication",
                  })}
                </GoabButton>
              )}
          </GoabBlock>
        </div>
        <GoabModal
          calloutVariant="important"
          role="alertdialog"
          heading={getResourceValue({
            key: "RESOURCES@withdrawal.confirm.callout",
          })}
          open={showWithdrawApplicationConfirmation}
        >
          <DisplayContent
            content={getResourceValue({
              key: "RESOURCES@withdrawal.confirm.callout.text",
            })}
          />
          <GoabSpacer vSpacing="xl" />
          <GoabButtonGroup alignment="end">
            <GoabButton
              type="secondary"
              onClick={() => setShowWithdrawApplicationConfirmation(false)}
            >
              {getResourceValue({ key: "RESOURCES@global.button.cancel" })}
            </GoabButton>
            <GoabButton onClick={() => closeWithdrawApplicationConfirmation()}>
              {getResourceValue({ key: "RESOURCES@global.button.confirm" })}
            </GoabButton>
          </GoabButtonGroup>
        </GoabModal>
      </GoabContainer>
    </>
  );
};

export default ApplicationComponent;